import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    BoundingBoxData,
    EventPlaybackRange,
    HighlightRegion,
    LoopWindow,
    PictureRect,
    TimeDomain,
} from '@/types/video-player';
import { FullSource } from '@/types/source';
import { StreamingMode, StreamingTimeline } from '@/types/streaming';
import { buildEventDisplayTimeline } from '@/utils/camera-event-timeline';
import { calculateTimeWindow } from '@/utils/camera-stream';
import {
    calculateSkipPosition,
    getEventHighlight,
    getFocusHighlights,
    getVideoElementFromRef,
} from '@/utils/video-player';
import { buildLoopWindow } from '@/utils/event-playback-range';
import {
    getMediaErrorMessage,
    MEDIA_ERROR_TOAST_ID,
    translateMediaError,
} from '@/utils/error-messages';
import { toast } from '@/components';
import { useStreamingMode } from './hooks/useStreamingMode';
import { useWebRTC } from './hooks/useWebRTC';
import { useSkipIndicator, SkipDirection } from './hooks/useSkipIndicator';
import { useTimelineGapState } from './hooks/useTimelineGapState';
import {
    MILLISECONDS_TO_SECONDS,
    STREAMING_ERRORS,
    STREAMING_TIMING,
} from '@/constants/time';
import { useManifestBeginTimestamp } from './hooks/useManifestBeginTimestamp';
import { useVideoPictureRect } from './hooks/useVideoPictureRect';
import { VideoPlayerEventEmitter } from './VideoPlayerEventEmitter';

interface StreamingContext {
    live: boolean;
    canGoLive: boolean;
    hasArchive: boolean;
    shouldUseWebRTC: boolean;
    videoSrc: string | null;
    status: string;
    timeline: StreamingTimeline | null;
    streamBeginTimestamp: number | null;
    pendingSeekTimestamp: number | null;
    storageService: string | null;
    onSeek: (time: number) => void;
    onSeekToTimestamp: (timestamp: number) => void;
    onGoLive: () => void;
    onExitLive: () => void;
}

interface SkipIndicatorState {
    isVisible: boolean;
    direction: SkipDirection | null;
    accumulatedSeconds: number;
}

interface VideoPlayerContextValue {
    mediaRef: React.RefObject<HTMLVideoElement | null>;
    webrtcRef: React.RefCallback<HTMLVideoElement>;
    activeMediaRef: React.RefObject<HTMLVideoElement | null>;
    playbackRate: number;
    setPlaybackRate: (rate: number) => void;
    containerRef: React.RefObject<HTMLDivElement | null>;
    expandedRootRef: React.RefObject<HTMLDivElement | null>;
    expanded: boolean;
    toggleExpanded: () => void;
    hovering: boolean;
    setHovering: (hovering: boolean) => void;
    streaming: StreamingContext;
    displayTimeline: StreamingTimeline | null;
    playbackStartTimestamp: number | undefined;
    pictureRect: PictureRect | null;
    isCamera: boolean;
    sourceId: string;
    sourceName: string;
    highlights: HighlightRegion[];
    loopTimeRange: LoopWindow | undefined;
    isTimelineMode: boolean;
    awaitingInitialPosition: boolean;
    showErrorState: boolean;
    streamUnavailable: boolean;
    skipIndicator: SkipIndicatorState;
    onSkip: (seconds: number, direction: SkipDirection) => void;
    skipTooltipTime: number | null;
    onError: (error: unknown) => void;
    playing: boolean;
    togglePlay: () => void;
    loop: boolean;
    setLoop: (loop: boolean) => void;
    cropEnabled: boolean;
    croppingMode: boolean;
    setCroppingMode: (active: boolean) => void;
    zoomed: boolean;
    toggleZoom: () => void;
    zoomLocked: boolean;
    toggleZoomLock: () => void;
    zoomFactor: number;
    emitter: VideoPlayerEventEmitter;
    loopSeekingRef: React.MutableRefObject<boolean>;
}

const VideoPlayerContext = createContext<VideoPlayerContextValue | null>(null);

export function useVideoPlayerContext(): VideoPlayerContextValue {
    const context = useContext(VideoPlayerContext);
    if (!context) {
        throw new Error(
            'useVideoPlayerContext must be used within VideoPlayerProvider',
        );
    }
    return context;
}

interface VideoPlayerProviderProps {
    children: React.ReactNode;
    boundingBoxProps?: BoundingBoxData;
    src: string;
    source?: FullSource;
    startTime?: number;
    initialMode?: StreamingMode;
    defaultLoop?: boolean;
    onDurationChange?: (duration: number) => void;
    onTimeUpdate?: (currentTime: number) => void;
    eventRange?: EventPlaybackRange;
    onPlayingChange?: (isPlaying: boolean) => void;
    onLoopChange?: (loopEnabled: boolean) => void;
    onSeeked?: () => void;
    autoPlay?: boolean;
    pauseSignal?: number;
    cropEnabled?: boolean;
}

const SKIP_TOOLTIP_DURATION_MS = 1500;
const ZOOM_FACTOR = 2;
const INITIAL_POSITION_TIMEOUT_MS = 5000;
const SEEK_SETTLE_TOLERANCE_SECONDS = 0.5;

export function VideoPlayerProvider({
    children,
    boundingBoxProps,
    src,
    source,
    startTime,
    initialMode,
    defaultLoop,
    onDurationChange,
    onTimeUpdate,
    eventRange,
    onPlayingChange,
    onLoopChange,
    onSeeked,
    autoPlay = true,
    pauseSignal,
    cropEnabled = false,
}: VideoPlayerProviderProps) {
    const mediaRef = useRef<HTMLVideoElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const expandedRootRef = useRef<HTMLDivElement>(null);
    const [emitter] = useState(() => new VideoPlayerEventEmitter());

    const [expanded, setExpanded] = useState(false);
    const [hovering, setHovering] = useState(false);
    const [playing, setPlaying] = useState(autoPlay);
    const [loop, setLoop] = useState(defaultLoop ?? false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [croppingMode, setCroppingModeState] = useState(false);
    const wasPlayingBeforeCroppingRef = useRef(false);
    const [zoomed, setZoomed] = useState(false);
    const [zoomLocked, setZoomLocked] = useState(false);
    const [skipTooltipTime, setSkipTooltipTime] = useState<number | null>(null);
    const skipTooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const absolutePositionRef = useRef<number | null>(null);
    const seekTargetRef = useRef<number | null>(null);
    const loopSeekingRef = useRef(false);
    const seekCorrectionRef = useRef<number | null>(null);
    const armedPlaybackStartRef = useRef<number | null>(null);
    const [awaitingPositionTarget, setAwaitingPositionTarget] = useState<
        number | null
    >(null);
    const [positionedVideoSrc, setPositionedVideoSrc] = useState<string | null>(
        null,
    );

    const isCameraSource = source?.type === 'CAMERA';
    const sourceId = source?.id || '';
    const sourceName = source?.displayName || source?.name || '';

    const highlights = useMemo(() => {
        if (!eventRange) return [];
        const eventHighlight = getEventHighlight(
            eventRange,
            boundingBoxProps?.id ?? 'event-range',
        );
        const focusHighlights = boundingBoxProps
            ? getFocusHighlights(boundingBoxProps, eventRange.domain)
            : [];
        return [eventHighlight, ...focusHighlights];
    }, [boundingBoxProps, eventRange]);

    const loopTimeRange = useMemo(
        () => (eventRange ? buildLoopWindow(eventRange) : undefined),
        [eventRange],
    );

    const loopAdjustedStartTime = useMemo(() => {
        if (!isCameraSource || !loopTimeRange) return startTime;
        return loopTimeRange.startTime;
    }, [isCameraSource, loopTimeRange, startTime]);

    useEffect(() => {
        if (!isCameraSource || !loopTimeRange) {
            armedPlaybackStartRef.current = null;
            seekCorrectionRef.current = null;
            setAwaitingPositionTarget(null);
            return;
        }
        if (loopTimeRange.domain !== TimeDomain.ABSOLUTE) return;
        if (armedPlaybackStartRef.current === loopTimeRange.playbackStart) {
            return;
        }
        armedPlaybackStartRef.current = loopTimeRange.playbackStart;
        seekCorrectionRef.current = loopTimeRange.playbackStart;
        setPositionedVideoSrc(null);
        setAwaitingPositionTarget(loopTimeRange.playbackStart);
    }, [isCameraSource, loopTimeRange]);

    const streamingMode = useStreamingMode({
        source,
        startTime: loopAdjustedStartTime,
        initialMode,
    });

    const webrtc = useWebRTC({
        sourceId,
        enabled: streamingMode.shouldUseWebRTC,
    });

    const activeMediaRef = streamingMode.shouldUseWebRTC
        ? webrtc.videoRef
        : mediaRef;

    const videoSrc = streamingMode.shouldUseWebRTC
        ? null
        : streamingMode.videoSrc || (isCameraSource ? null : src);

    const manifestBeginTimestamp = useManifestBeginTimestamp({
        videoRef: activeMediaRef,
        emitter,
        videoSrc,
    });

    const streamBeginTimestamp =
        manifestBeginTimestamp ?? streamingMode.streamBeginTimestamp;

    const [previousVideoSrc, setPreviousVideoSrc] = useState(videoSrc);
    if (previousVideoSrc !== videoSrc) {
        setPreviousVideoSrc(videoSrc);
        setPositionedVideoSrc(null);
    }
    const requestedTimeWindow =
        streamingMode.targetTimestamp == null
            ? null
            : calculateTimeWindow(
                  streamingMode.targetTimestamp,
                  STREAMING_TIMING.DEFAULT_VOD_WINDOW_MS,
              );
    const pendingStreamTarget =
        requestedTimeWindow !== null &&
        streamingMode.streamBeginTimestamp !==
            requestedTimeWindow.beginMilliseconds / MILLISECONDS_TO_SECONDS;
    const pendingSeekTimestamp =
        isCameraSource &&
        !streamingMode.live &&
        (!videoSrc || positionedVideoSrc !== videoSrc || pendingStreamTarget)
            ? (streamingMode.targetTimestamp ?? loopAdjustedStartTime ?? null)
            : null;

    useEffect(() => {
        setPlaying(autoPlay);
        setPlaybackRate(1);
        setCroppingModeState(false);
        wasPlayingBeforeCroppingRef.current = false;
        setZoomed(false);
        setZoomLocked(false);
        onPlayingChange?.(autoPlay);
        emitter.emit('sourcechange', seekTargetRef.current ?? undefined);
    }, [videoSrc, emitter, autoPlay, onPlayingChange]);

    useEffect(() => {
        const unsubscribePlay = emitter.on('play', () => {
            setPlaying(true);
            onPlayingChange?.(true);
        });
        const unsubscribePause = emitter.on('pause', () => {
            setPlaying(false);
            onPlayingChange?.(false);
        });
        return () => {
            unsubscribePlay();
            unsubscribePause();
        };
    }, [emitter, onPlayingChange]);

    const lastPauseSignalRef = useRef(pauseSignal);
    useEffect(() => {
        if (pauseSignal === undefined) return;
        if (lastPauseSignalRef.current === pauseSignal) return;
        lastPauseSignalRef.current = pauseSignal;
        setPlaying(false);
        onPlayingChange?.(false);
    }, [pauseSignal, onPlayingChange]);

    useEffect(() => {
        if (!onSeeked) return;
        return emitter.on('seeked', () => {
            if (loopSeekingRef.current) {
                loopSeekingRef.current = false;
                return;
            }
            onSeeked();
        });
    }, [emitter, onSeeked]);

    useEffect(() => {
        onLoopChange?.(loop);
    }, [loop, onLoopChange]);

    useEffect(() => {
        if (streamingMode.live) {
            setLoop(false);
            seekTargetRef.current = null;
            seekCorrectionRef.current = null;
        }
    }, [streamingMode.live]);

    useEffect(() => {
        setLoop(defaultLoop ?? false);
    }, [defaultLoop]);

    useEffect(() => {
        if (!onDurationChange && !onTimeUpdate) return;

        const unsubscribes: (() => void)[] = [];

        if (onTimeUpdate) {
            unsubscribes.push(
                emitter.on('timeupdate', () => {
                    const videoElement = getVideoElementFromRef(activeMediaRef);
                    if (!videoElement) return;
                    if (isCameraSource && streamBeginTimestamp != null) {
                        onTimeUpdate(
                            streamBeginTimestamp + videoElement.currentTime,
                        );
                        return;
                    }
                    onTimeUpdate(videoElement.currentTime);
                }),
            );
        }

        if (onDurationChange) {
            unsubscribes.push(
                emitter.on('durationchange', () => {
                    const videoElement = getVideoElementFromRef(activeMediaRef);
                    if (!videoElement) return;
                    const duration = videoElement.duration;
                    if (
                        !isNaN(duration) &&
                        isFinite(duration) &&
                        duration > 0
                    ) {
                        onDurationChange(duration);
                    }
                }),
            );
        }

        return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
    }, [
        emitter,
        activeMediaRef,
        onDurationChange,
        onTimeUpdate,
        isCameraSource,
        streamBeginTimestamp,
    ]);

    useEffect(() => {
        if (!isCameraSource) return;

        const unsubscribe = emitter.on('timeupdate', () => {
            if (streamBeginTimestamp == null) return;

            if (seekTargetRef.current != null) {
                return;
            }

            const videoElement = getVideoElementFromRef(activeMediaRef);
            if (!videoElement) return;
            absolutePositionRef.current =
                streamBeginTimestamp + videoElement.currentTime;
        });

        return unsubscribe;
    }, [emitter, activeMediaRef, isCameraSource, streamBeginTimestamp]);

    useEffect(() => {
        if (
            !isCameraSource ||
            streamingMode.live ||
            manifestBeginTimestamp == null ||
            !videoSrc ||
            positionedVideoSrc === videoSrc ||
            pendingStreamTarget
        )
            return;

        const settlePosition = () => {
            const target =
                seekCorrectionRef.current ??
                streamingMode.targetTimestamp ??
                streamingMode.streamBeginTimestamp;
            if (target == null) return;

            const videoElement = getVideoElementFromRef(activeMediaRef);
            if (!videoElement) return;

            const { seekable } = videoElement;
            if (seekable.length === 0) return;
            const offset = Math.max(
                target - manifestBeginTimestamp,
                seekable.start(0),
            );
            if (offset > seekable.end(seekable.length - 1)) return;

            if (
                Math.abs(videoElement.currentTime - offset) <=
                SEEK_SETTLE_TOLERANCE_SECONDS
            ) {
                seekCorrectionRef.current = null;
                seekTargetRef.current = null;
                absolutePositionRef.current =
                    manifestBeginTimestamp + videoElement.currentTime;
                setPositionedVideoSrc(videoSrc);
                setAwaitingPositionTarget(null);
                return;
            }

            loopSeekingRef.current = true;
            videoElement.currentTime = offset;
            absolutePositionRef.current = target;
        };

        settlePosition();

        const unsubscribes = [
            emitter.on('timeupdate', settlePosition),
            emitter.on('loadedmetadata', settlePosition),
            emitter.on('ready', settlePosition),
            emitter.on('seeked', settlePosition),
        ];

        return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
    }, [
        emitter,
        activeMediaRef,
        isCameraSource,
        manifestBeginTimestamp,
        videoSrc,
        positionedVideoSrc,
        streamingMode.live,
        streamingMode.targetTimestamp,
        streamingMode.streamBeginTimestamp,
        pendingStreamTarget,
    ]);

    useEffect(() => {
        if (awaitingPositionTarget === null) return;
        const timer = setTimeout(() => {
            seekCorrectionRef.current = null;
            setAwaitingPositionTarget(null);
        }, INITIAL_POSITION_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [awaitingPositionTarget]);

    const isTimelineMode =
        streamingMode.live ||
        streamingMode.timeline !== null ||
        eventRange?.domain === TimeDomain.ABSOLUTE;

    const showErrorState =
        streamingMode.status === 'error' && !streamingMode.live;

    const toggleExpanded = useCallback(() => {
        setExpanded((prev) => !prev);
    }, []);

    const togglePlay = useCallback(() => {
        setPlaying((prev) => !prev);
    }, []);

    const setCroppingMode = useCallback(
        (active: boolean) => {
            if (active && !cropEnabled) return;
            setCroppingModeState((current) => {
                if (current === active) return current;
                if (active) {
                    setZoomed(false);
                    wasPlayingBeforeCroppingRef.current = playing;
                    if (playing) {
                        setPlaying(false);
                        onPlayingChange?.(false);
                    }
                } else if (wasPlayingBeforeCroppingRef.current) {
                    setPlaying(true);
                    onPlayingChange?.(true);
                }
                return active;
            });
        },
        [cropEnabled, playing, onPlayingChange],
    );

    const toggleZoom = useCallback(() => {
        setZoomed((current) => {
            const next = !current;
            if (next) {
                // Entering zoom exits crop mode; route through setCroppingMode
                // so its resume logic (restoring playback that was paused for
                // cropping) runs instead of being bypassed.
                setCroppingMode(false);
            } else {
                setZoomLocked(false);
            }
            return next;
        });
    }, [setCroppingMode]);

    const toggleZoomLock = useCallback(() => {
        setZoomLocked((current) => !current);
    }, []);

    useEffect(() => {
        if (!cropEnabled && croppingMode) {
            setCroppingModeState(false);
            wasPlayingBeforeCroppingRef.current = false;
        }
    }, [cropEnabled, croppingMode]);

    const onError = useCallback(async (error: unknown) => {
        console.error('Video player error:', error);
        const asEvent = error as { target?: HTMLElement };
        if (asEvent?.target) {
            const errorMessage = await getMediaErrorMessage(
                error as Event | React.SyntheticEvent,
            );
            if (errorMessage) {
                toast.error(errorMessage, { id: MEDIA_ERROR_TOAST_ID });
            }
        } else {
            toast.error(translateMediaError('playbackFailed'), {
                id: MEDIA_ERROR_TOAST_ID,
            });
        }
    }, []);

    const skipIndicator = useSkipIndicator({
        enabled: !streamingMode.live,
    });

    const onSeekToTimestamp = useCallback(
        (timestamp: number) => {
            const resolvedTimestamp =
                streamingMode.onSeekToTimestamp(timestamp) ?? timestamp;
            seekTargetRef.current = resolvedTimestamp;
            seekCorrectionRef.current = resolvedTimestamp;
            absolutePositionRef.current = resolvedTimestamp;
            setPositionedVideoSrc(null);
        },
        [streamingMode.onSeekToTimestamp],
    );

    const onSkip = useCallback(
        (seconds: number, direction: SkipDirection) => {
            const positionInRange = isCameraSource
                ? (absolutePositionRef.current ?? streamBeginTimestamp ?? 0) -
                  (streamingMode.timeline?.earliest ?? 0)
                : ((activeMediaRef.current as HTMLVideoElement | null)
                      ?.currentTime ?? undefined);

            if (isCameraSource) {
                const currentPos =
                    absolutePositionRef.current ?? streamBeginTimestamp ?? 0;
                const targetPosition = calculateSkipPosition(
                    currentPos,
                    seconds,
                    streamingMode.timeline?.earliest ?? null,
                    Date.now() / MILLISECONDS_TO_SECONDS,
                );

                onSeekToTimestamp(targetPosition);
                setSkipTooltipTime(targetPosition);
            } else {
                const videoElement =
                    activeMediaRef.current as HTMLVideoElement | null;
                if (videoElement?.duration != null) {
                    const time = videoElement.currentTime ?? 0;
                    const clampedTime = Math.max(
                        0,
                        Math.min(videoElement.duration, time + seconds),
                    );
                    videoElement.currentTime = clampedTime;

                    setSkipTooltipTime(clampedTime);
                }
            }

            skipIndicator.handleSkip(seconds, direction, positionInRange);

            if (skipTooltipTimeoutRef.current) {
                clearTimeout(skipTooltipTimeoutRef.current);
            }

            skipTooltipTimeoutRef.current = setTimeout(() => {
                setSkipTooltipTime(null);
            }, SKIP_TOOLTIP_DURATION_MS);
        },
        [
            streamingMode,
            skipIndicator,
            isCameraSource,
            activeMediaRef,
            streamBeginTimestamp,
            onSeekToTimestamp,
        ],
    );

    const { streamUnavailable: inTimelineGap } = useTimelineGapState({
        absoluteTimestamp:
            absolutePositionRef.current ?? loopAdjustedStartTime ?? null,
        timeline: streamingMode.timeline,
        isLive: streamingMode.live,
        isCameraSource,
        streamBeginTimestamp,
        videoRef: activeMediaRef,
        emitter,
    });
    const streamUnavailable =
        inTimelineGap ||
        (showErrorState &&
            streamingMode.error === STREAMING_ERRORS.NO_RECORDING);

    const pictureRect = useVideoPictureRect({
        videoRef: activeMediaRef,
        emitter,
    });

    const streaming: StreamingContext = useMemo(
        () => ({
            live: streamingMode.live,
            canGoLive: streamingMode.canGoLive,
            hasArchive: streamingMode.hasArchive,
            shouldUseWebRTC: streamingMode.shouldUseWebRTC,
            videoSrc,
            status: streamingMode.status,
            timeline: streamingMode.timeline,
            streamBeginTimestamp,
            pendingSeekTimestamp,
            storageService: streamingMode.storageService,
            onSeek: streamingMode.onSeek,
            onSeekToTimestamp,
            onGoLive: streamingMode.onGoLive,
            onExitLive: streamingMode.onExitLive,
        }),
        [
            streamingMode.live,
            streamingMode.canGoLive,
            streamingMode.hasArchive,
            streamingMode.shouldUseWebRTC,
            videoSrc,
            streamingMode.status,
            streamingMode.timeline,
            streamBeginTimestamp,
            pendingSeekTimestamp,
            streamingMode.storageService,
            streamingMode.onSeek,
            onSeekToTimestamp,
            streamingMode.onGoLive,
            streamingMode.onExitLive,
        ],
    );

    const displayTimeline = buildEventDisplayTimeline(
        streaming.timeline,
        eventRange,
    );

    const value = useMemo<VideoPlayerContextValue>(
        () => ({
            mediaRef,
            webrtcRef: webrtc.videoCallbackRef,
            activeMediaRef,
            playbackRate,
            setPlaybackRate,
            containerRef,
            expandedRootRef,
            expanded,
            toggleExpanded,
            hovering,
            setHovering,
            streaming,
            displayTimeline,
            pictureRect,
            playbackStartTimestamp:
                streamingMode.targetTimestamp ?? loopAdjustedStartTime,
            isCamera: isCameraSource,
            sourceId,
            sourceName,
            highlights,
            loopTimeRange,
            isTimelineMode,
            awaitingInitialPosition: awaitingPositionTarget !== null,
            showErrorState,
            streamUnavailable,
            skipIndicator,
            onSkip,
            skipTooltipTime,
            onError,
            playing,
            togglePlay,
            loop,
            setLoop,
            cropEnabled,
            croppingMode,
            setCroppingMode,
            zoomed,
            toggleZoom,
            zoomLocked,
            toggleZoomLock,
            zoomFactor: ZOOM_FACTOR,
            emitter,
            loopSeekingRef,
        }),
        [
            activeMediaRef,
            playbackRate,
            setPlaybackRate,
            expanded,
            toggleExpanded,
            hovering,
            playing,
            loop,
            cropEnabled,
            croppingMode,
            setCroppingMode,
            zoomed,
            toggleZoom,
            zoomLocked,
            toggleZoomLock,
            streaming,
            displayTimeline,
            pictureRect,
            loopAdjustedStartTime,
            streamingMode.targetTimestamp,
            isCameraSource,
            sourceId,
            sourceName,
            highlights,
            loopTimeRange,
            isTimelineMode,
            awaitingPositionTarget,
            showErrorState,
            streamUnavailable,
            skipIndicator,
            onSkip,
            skipTooltipTime,
            onError,
            togglePlay,
            emitter,
        ],
    );

    return (
        <VideoPlayerContext.Provider value={value}>
            {children}
        </VideoPlayerContext.Provider>
    );
}
